import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Storage } from "../../storage";
import { createVenueNote, updateVenueNote } from "../../apis/venue-notes";

export function useCreatePublicVenueNote(options: {
  /**
   * When `false`, skip invalidating `loadVenueDetailsBySlug` on success.
   * `useVenueDetailsBySlug` exposes refetches as `isLoading`, so a host that
   * gates rendering on it (e.g. the complex Space-details drawer) would flash
   * a full-page spinner. Such callers pass `false` and refresh their own view
   * — the complex drawer reads its alert list from `useAllMyVenueNotes`, which
   * the edit modal invalidates separately. Defaults to `true`.
   */
  invalidateVenueDetails?: boolean;
} = {}) {
  const { invalidateVenueDetails = true } = options;
  const queryClient = useQueryClient();

  const { data, error, mutate, mutateAsync, status } = useMutation({
    mutationFn: ({
      title,
      noteId,
      businessId,
      note,
      notePriority,
      noteAreaType,
      noteAreaId,
      dateFrom,
      dateTo,
      business_note_heading_suggestions_id,
      placeholder_text,
    }: {
      title: string;
      noteId?: string;
      businessId: string;
      note: string;
      notePriority: string;
      noteAreaType: string;
      noteAreaId: string;
      dateFrom: string;
      dateTo: string;
      business_note_heading_suggestions_id?: string;
      placeholder_text?: string;
    }) => {
      if (noteId) {
        return updateVenueNote({
          noteId,
          businessId,
          title,
          note,
          notePriority,
          noteAreaType,
          noteAreaId,
          dateFrom,
          dateTo,
          business_note_heading_suggestions_id,
          placeholder_text,
          token: Storage.getCurrentUserToken() || "",
        });
      } else {
        return createVenueNote({
          businessId,
          title,
          note,
          notePriority,
          noteAreaType,
          noteAreaId,
          dateFrom,
          dateTo,
          business_note_heading_suggestions_id,
          placeholder_text,
          token: Storage.getCurrentUserToken() || "",
        });
      }
    },
    onSuccess: async () => {
      if (invalidateVenueDetails) {
        await queryClient.invalidateQueries({
          queryKey: ["loadVenueDetailsBySlug"],
          exact: false,
        });
      }
    },
  });
  return {
    data,
    error,
    mutate,
    mutateAsync,
    status,
  };
}
